import { ImpactProviderMapResults, MemberMapResults, NetworkProviderMapResults } from "@app/projects/results/common/mapping";
import { createContext, FC, useContext, useState, useEffect } from "react";
import { CountyProjectContext, ProjectService } from "@app/projects";
import { FilterContext } from "./FilterContext";
import { NetworkAssessmentAnalysisType, ProjectionProviderType, ProviderNetworkType, RegionSetStatusType } from "@app/projects/enumerators";
import { GainsPercentEnum } from "@app/projects/servicing-provider/types";
import { Feature, FeatureCollection, Point } from "geojson";
import MemberSetService from "@app/members/memberSetService";
import GeographyService from "@app/geography/geographyService";
import { bbox } from "@turf/bbox";
import * as ReactMapGL from "react-map-gl/mapbox";
import { LngLatBounds } from "mapbox-gl"
import { AdequacySummarySpecialtyGroup, AdequacySummaryByRegionSet } from "@app/projects/results/adequacy";
import { ImpactProvidersFilterOptions, ProviderAccordionSelectedTab, ProviderSourceOfSelection, ProvidersSortOptions } from "../enumerators";
import { MappingContext } from "@app/mapping/MappingContext";

interface NetworkCoverageMapDataContextProps {
  countyBorderData: FeatureCollection | null;
  currentAdequacySummarySpecialtyGroup: AdequacySummarySpecialtyGroup;
  currentMapBoundary: ReactMapGL.LngLatBoundsLike | null;
  dataRefreshCounter: number;
  dataBoundingBox: ReactMapGL.LngLatBoundsLike | null;
  dataLoaded: boolean;
  filterImpactProvidersByNetworkAndActionType: (propviders: ImpactProviderMapResults[]) => ImpactProviderMapResults[];
  loadingMembers: boolean;
  loadingProviders: boolean;
  networkProviders: NetworkProviderMapResults[] | null;
  impactProviders: ImpactProviderMapResults[] | null;
  isProviderPopupOpen: boolean,
  passPercentage: number | null;
  providerAccordionSelectedTab: ProviderAccordionSelectedTab;
  providerFetchMessage: string | null;
  providerSearchText: string;
  selectedProviderFilterOptions: ImpactProvidersFilterOptions[];
  selectedProvider: NetworkProviderMapResults | ImpactProviderMapResults;
  selectedProviderSourceOfSelection: ProviderSourceOfSelection;
  mapStyle: string;
  memberData: FeatureCollection<Point, MemberMapResults> | null;
  visibleImpactProviders: ImpactProviderMapResults[];
  visibleNetworkProviders: NetworkProviderMapResults[];
  setCurrentAdequacySummarySpecialtyGroup: (adequacySummarySpecialtyGroup: AdequacySummarySpecialtyGroup) => void;
  setCurrentMapBoundary: React.Dispatch<React.SetStateAction<ReactMapGL.LngLatBoundsLike>>,
  setDataRefreshCounter: React.Dispatch<React.SetStateAction<number>>,
  setIsProviderPopupOpen: React.Dispatch<React.SetStateAction<boolean>>,
  selectedProviderSortOption: ProvidersSortOptions;
  setLoadingProviders: (loading: boolean) => void;
  setMapStyle: React.Dispatch<React.SetStateAction<string>>;
  setNetworkProviders: (providers: NetworkProviderMapResults[] | null) => void;
  setImpactProviders: (providers: ImpactProviderMapResults[] | null) => void;
  setPassPercentage: (percentage: number | null) => void;
  setProviderAccordionSelectedTab:  React.Dispatch<React.SetStateAction<ProviderAccordionSelectedTab>>;
  setProviderFetchMessage: (message: string | null) => void;
  setProviderSearchText: React.Dispatch<React.SetStateAction<string>>;
  setSelectedProviderFilterOptions: React.Dispatch<React.SetStateAction<ImpactProvidersFilterOptions[]>>;
  setSelectedProvider: React.Dispatch<React.SetStateAction<NetworkProviderMapResults | ImpactProviderMapResults>>,
  setSelectedProviderSourceOfSelection: React.Dispatch<React.SetStateAction<ProviderSourceOfSelection>>,
  setSelectedProviderSortOption: React.Dispatch<React.SetStateAction<ProvidersSortOptions>>,
  setShowMembersWithAccess: React.Dispatch<React.SetStateAction<boolean>>,
  setShowMembersWithoutAccess: React.Dispatch<React.SetStateAction<boolean>>,
  setVisibleImpactProviders: React.Dispatch<React.SetStateAction<ImpactProviderMapResults[]>>,
  setVisibleNetworkProviders: React.Dispatch<React.SetStateAction<NetworkProviderMapResults[]>>,
  showMembersWithAccess: boolean,
  showMembersWithoutAccess: boolean,
  sortProviders: <T extends NetworkProviderMapResults | ImpactProviderMapResults>(providers: T[], sortOption: ProvidersSortOptions) => T[];
  getRegionDetails: () => void;
  resetMapData: () => void;
}

const NetworkCoverageMapDataContext = createContext<Partial<NetworkCoverageMapDataContextProps>>({});

const NetworkCoverageMapDataContextProvider: FC<{ children: React.ReactNode }> = (props: { children: React.ReactNode }) => {
  const projectService = new ProjectService();

  const projectContext = useContext(CountyProjectContext);
  const filterContext = useContext(FilterContext);
  const mappingContext = useContext(MappingContext);

  /* Data loading related values */
  const [dataLoaded, setDataLoaded] = useState<boolean>(false);
  const [loadingCountyBorders, setLoadingCountyBorders] = useState<boolean>(false);
  const [loadingMembers, setLoadingMembers] = useState<boolean>(false);
  const [loadingProviders, setLoadingProviders] = useState<boolean>(false);
  const [providerFetchMessage, setProviderFetchMessage] = useState<string | null>('No providers found for the selected filters.');
  const [dataRefreshCounter, setDataRefreshCounter] = useState<number>(0);

  /* Providers data */
  const [networkProviders, setNetworkProviders] = useState<NetworkProviderMapResults[] | null>(null);
  const [visibleNetworkProviders, setVisibleNetworkProviders] = useState<NetworkProviderMapResults[]>([]);
  const [impactProviders, setImpactProviders] = useState<ImpactProviderMapResults[] | null>(null);
  const [visibleImpactProviders, setVisibleImpactProviders] = useState<ImpactProviderMapResults[]>([]);

  /* Provider search, filter and sort related values */ 
  const [selectedProviderFilterOptions, setSelectedProviderFilterOptions] = useState<ImpactProvidersFilterOptions[]>([ImpactProvidersFilterOptions.Market, ImpactProvidersFilterOptions.Prospect]);
  const [selectedProviderSortOption, setSelectedProviderSortOption] = useState<ProvidersSortOptions>(ProvidersSortOptions.Alphabetical);
  const [providerSearchText, setProviderSearchText] = useState<string>('');
  const [providerAccordionSelectedTab, setProviderAccordionSelectedTab] = useState(ProviderAccordionSelectedTab.NetworkProviders);
  const [selectedProvider, setSelectedProvider] = useState<NetworkProviderMapResults | ImpactProviderMapResults>(null);
  const [selectedProviderSourceOfSelection, setSelectedProviderSourceOfSelection] = useState<ProviderSourceOfSelection>(ProviderSourceOfSelection.Unknown);

  /* Members data */
  const [memberData, setMemberData] = useState<FeatureCollection<Point, MemberMapResults> | null>(null);
  const [showMembersWithAccess, setShowMembersWithAccess] = useState<boolean>(true);
  const [showMembersWithoutAccess, setShowMembersWithoutAccess] = useState<boolean>(true);

  /* Map related values */
  const [countyBorderData, setCountyBorderData] = useState<FeatureCollection | null>(null);
  const [dataBoundingBox, setDataBoundingBox] = useState<ReactMapGL.LngLatBoundsLike | null>(null);
  const [currentMapBoundary, setCurrentMapBoundary] = useState<ReactMapGL.LngLatBoundsLike | null>(null);
  const [mapStyle, setMapStyle] = useState<string>(mappingContext.qesOutdoorsStyle);

  /* Miscellaneous */
  const [passPercentage, setPassPercentage] = useState<number | null>(null);
  const [currentAdequacySummarySpecialtyGroup, setCurrentAdequacySummarySpecialtyGroup] = useState<AdequacySummarySpecialtyGroup | null>(null);
  const [isProviderPopupOpen, setIsProviderPopupOpen] = useState(false);
  
  const memberSetService = new MemberSetService();
  const geographyService = new GeographyService();

  const getRegionDetails = async () => {
    const regionDetails: AdequacySummaryByRegionSet[] =
      await projectService.regionDetailsByRegionSet(
        projectContext.project.projectId,
        Number(filterContext.selectedServiceArea),
        projectContext.runConfiguration.projectRunConfigurationId,
        filterContext.selectedSpecialty,
        filterContext.getCurrentServiceAreaStatus() == RegionSetStatusType.Opportunity ? NetworkAssessmentAnalysisType.Opportunity : NetworkAssessmentAnalysisType.ServiceArea);

    const selectedSpecialtyGroupSummary = regionDetails[0].specialtyGroupSummaries[0];
    setCurrentAdequacySummarySpecialtyGroup(selectedSpecialtyGroupSummary);
  }

  const fetchMemberData = async (): Promise<void> => {
    if (!filterContext.selectedServiceArea || !filterContext.selectedSpecialty) {
      setMemberData(null);
      setLoadingMembers(false);
      return;
    }

    try {
      const fetchedMemberData = await memberSetService.GetMemberResultsAsync(
        projectContext.project.projectId,
        projectContext.runConfiguration.projectRunConfigurationId,
        filterContext.selectedServiceArea,
        filterContext.selectedSpecialty
      );

      setMemberData(fetchedMemberData);
      setLoadingMembers(false);
    } catch (error) {
      setMemberData(null);
      setLoadingMembers(false);
    }
  };

  useEffect(() => {
    setPassPercentage(projectContext.adequacyResult?.details[0]?.accessSummary?.passPercentage || null);
  }, [projectContext.adequacyResult]);

  useEffect(() => {
    if (!filterContext.isFilterActive) {
      setNetworkProviders(null);
      setImpactProviders(null);
      setProviderFetchMessage(null);
      setLoadingCountyBorders(false);
      setLoadingMembers(false);
      setLoadingProviders(false);
      setPassPercentage(null);
      setCurrentAdequacySummarySpecialtyGroup(null);
      setMemberData(null);
      setCountyBorderData(null);
      setDataBoundingBox(null);
      setCurrentMapBoundary(null);
    } else {
      setLoadingCountyBorders(true);
      setLoadingMembers(true);
      setLoadingProviders(true);

      const getProviders = async (providerNetworkType: ProviderNetworkType) => {
        const result = await projectService.servicingAccessPoints(
          projectContext.project.projectId,
          filterContext.selectedServiceArea,
          projectContext.runConfiguration.projectRunConfigurationId,
          filterContext.selectedSpecialty,
          providerNetworkType,
          filterContext.getCurrentServiceAreaStatus() == RegionSetStatusType.Opportunity ? NetworkAssessmentAnalysisType.Opportunity : NetworkAssessmentAnalysisType.ServiceArea,
          GainsPercentEnum.All
        );

        if (!result || !result.features || result.features.length === 0) {
          setProviderFetchMessage("No providers found for the selected filters.");
          return;
        }

        if (providerNetworkType !== ProviderNetworkType.Network) {
          setImpactProviders((existing) =>
            [
              ...(existing || []),
              ...result.features.map((p) => ({ ...p.properties, providerNetworkType: providerNetworkType })) as ImpactProviderMapResults[]
            ]
          );
        } else {
          setNetworkProviders(result.features.map(p => p.properties));
        }
      };

      filterContext.selectedProviderSources.forEach((source) => {
        getProviders(source)
          .catch(() => {
            setProviderFetchMessage("Error fetching providers. Please try again.");
            setNetworkProviders([]);
            setImpactProviders([]);
          }).finally(() => {
            setLoadingProviders(false);
          })
      });

      getRegionDetails();

      // Fetch member data
      if (filterContext.selectedServiceArea && filterContext.selectedSpecialty) {
        fetchMemberData();
      } else {
        setMemberData(null);
        setLoadingMembers(false);
      }

      if (!(impactProviders || networkProviders)) {
        setProviderFetchMessage("No providers found for the selected filters.");
      }

      geographyService.getCountyBordersByRegionSetId(filterContext.selectedServiceArea)
        .then((countyBorderData: GeoJSON.FeatureCollection) => {
          setCountyBorderData(countyBorderData ?? { features: [], type: "FeatureCollection" })
          setLoadingCountyBorders(false);
        })
        .catch(() => {
          setCountyBorderData(null);
          setLoadingCountyBorders(false);
        });
    }
  }, [filterContext.isFilterActive, dataRefreshCounter]);

  useEffect(() => {
    setDataLoaded(!loadingCountyBorders && !loadingMembers && !loadingProviders && !!countyBorderData);
  }, [loadingCountyBorders, loadingMembers, loadingProviders, countyBorderData]);

  useEffect(() => {
    if (dataLoaded) {
      // bounding box calculation
      const boundingBoxOfFeatures: Feature[] = [];
      boundingBoxOfFeatures.push(...countyBorderData.features);

      if (!!networkProviders) {
        boundingBoxOfFeatures.push(
          ...networkProviders.map((provider): Feature<Point> => ({
            geometry: {
              coordinates: [provider.longitude, provider.latitude],
              type: "Point",
            },
            properties: [],
            type: "Feature"
          }))
        );
      }

      if (!!impactProviders) {
        boundingBoxOfFeatures.push(
          ...impactProviders.map((provider): Feature<Point> => ({
            geometry: {
              coordinates: [provider.longitude, provider.latitude],
              type: "Point",
            },
            properties: [],
            type: "Feature"
          }))
        );
      }

      // Calculate bounding box
      if (boundingBoxOfFeatures.length > 0) {
        const combinedFeatureCollection: FeatureCollection = {
          features: boundingBoxOfFeatures,
          type: "FeatureCollection",
        };

        const [minLng, minLat, maxLng, maxLat] = bbox(combinedFeatureCollection);

        // Fit map to bounding box
        setDataBoundingBox(new LngLatBounds([minLng, minLat], [maxLng, maxLat]));
      }
    }
  }, [dataLoaded]);

  useEffect(() => {
    
    if (!currentMapBoundary) return;

    /*** Searching and sorting logic for Network providers ***/
    let updatedVisibleNetWorkProviders = networkProviders?.filter(p => (currentMapBoundary as LngLatBounds).contains([p.longitude, p.latitude])) || [];
    // Take into account the applied search
    updatedVisibleNetWorkProviders = filterProvidersByNameAndNpi(providerSearchText, updatedVisibleNetWorkProviders);
    //Take into account the selected sort option
    updatedVisibleNetWorkProviders = sortProviders(updatedVisibleNetWorkProviders, selectedProviderSortOption);
    setVisibleNetworkProviders(updatedVisibleNetWorkProviders);

    /*** Searching, filtering and sorting logic for Out of Network providers ***/
    let updatedVisibleImpactProviders = impactProviders?.filter(p => (currentMapBoundary as LngLatBounds).contains([p.longitude, p.latitude])) || [];
    // Take into account the applied search
    updatedVisibleImpactProviders = filterProvidersByNameAndNpi(providerSearchText, updatedVisibleImpactProviders);
    // Take into account the selected filter options
    updatedVisibleImpactProviders = filterImpactProvidersByNetworkAndActionType(updatedVisibleImpactProviders);
    //Take into account the selected sort option
    updatedVisibleImpactProviders = sortProviders(updatedVisibleImpactProviders, selectedProviderSortOption);
    setVisibleImpactProviders(updatedVisibleImpactProviders);

    // Unselect a provider (its card and its icon) if after searching or filtering, it is not included in the result 
    if (!!selectedProvider && 
      (!!(selectedProvider as NetworkProviderMapResults) && !updatedVisibleNetWorkProviders.some(p => p.providerId === selectedProvider?.providerId)) &&
      (!!(selectedProvider as ImpactProviderMapResults) && !updatedVisibleImpactProviders.some(p => p.providerId === selectedProvider?.providerId && p.providerNetworkType === (selectedProvider as ImpactProviderMapResults).providerNetworkType))) {
      setSelectedProvider(null);
      setSelectedProviderSourceOfSelection(ProviderSourceOfSelection.Unknown);
    }
  }, [currentMapBoundary, networkProviders, impactProviders, providerSearchText, selectedProviderFilterOptions, selectedProviderSortOption, dataRefreshCounter]);

  const filterProvidersByNameAndNpi = <T extends { displayName: string; npi: string }>(searchText: string, providers: T[]): T[] => {
    if (!searchText)
      return providers;
    return providers.filter(provider =>
      (provider.displayName?.toLowerCase().includes(searchText.toLocaleLowerCase()) ?? false) || (provider.npi?.toLowerCase().includes(searchText.toLowerCase())??false)) 
  };

  const filterImpactProvidersByNetworkAndActionType = (providers: ImpactProviderMapResults[]): ImpactProviderMapResults[] => {
    if (!selectedProviderFilterOptions || selectedProviderFilterOptions.length === 0) {
      return []; // If no filters are selected, do not show any impact provider
    }

    if (!providers || providers.length === 0) {
      return []; // No providers to filter
    }

    return providers.filter(provider => {
      if (selectedProviderFilterOptions.includes(ImpactProvidersFilterOptions.TargetedMarket) && provider.projectionType === ProjectionProviderType.Target && provider.providerNetworkType === ProviderNetworkType.Market)
        return true;
      if (selectedProviderFilterOptions.includes(ImpactProvidersFilterOptions.TargetedProspect) && provider.projectionType === ProjectionProviderType.Target && provider.providerNetworkType === ProviderNetworkType.Prospect)
        return true;
      if (selectedProviderFilterOptions.includes(ImpactProvidersFilterOptions.Prospect) && provider.providerNetworkType === ProviderNetworkType.Prospect && provider.projectionType !== ProjectionProviderType.Target)
        return true;
      if (selectedProviderFilterOptions.includes(ImpactProvidersFilterOptions.Market) && provider.providerNetworkType === ProviderNetworkType.Market && provider.projectionType !== ProjectionProviderType.Target)
        return true;

      return false;
    });
  }

  const resetMapData = () => {
    setSelectedProviderFilterOptions([ImpactProvidersFilterOptions.Market, ImpactProvidersFilterOptions.Prospect]);
    setSelectedProviderSortOption(ProvidersSortOptions.Alphabetical);
    setProviderSearchText("");
    setSelectedProvider(null);
    setSelectedProviderSourceOfSelection(ProviderSourceOfSelection.Unknown);
    setNetworkProviders([]);
    setVisibleNetworkProviders([]);
    setImpactProviders([]);
    setVisibleImpactProviders([]);
    setShowMembersWithAccess(true);
    setShowMembersWithoutAccess(true);
    setMapStyle(mappingContext.qesOutdoorsStyle);
    setDataRefreshCounter(0);
  };


  const sortProviders = (
      <T extends NetworkProviderMapResults | ImpactProviderMapResults>(
        providers: T[],
        sortOption: ProvidersSortOptions
      ): T[] => {
        const sorted = [...providers];
        switch (sortOption) {
          case ProvidersSortOptions.Alphabetical:
            return sorted.sort((a, b) => a.displayName.localeCompare(b.displayName));
          case ProvidersSortOptions.ReverseAlphabetical:
            return sorted.sort((a, b) => b.displayName.localeCompare(a.displayName));
          case ProvidersSortOptions.ServesHighToLow:
            return sorted.sort((a, b) => (b as NetworkProviderMapResults).servesPercent - (a as NetworkProviderMapResults).servesPercent);
          case ProvidersSortOptions.ServesLowToHigh:
            return sorted.sort((a, b) => (a as NetworkProviderMapResults).servesPercent - (b as NetworkProviderMapResults).servesPercent);
          case ProvidersSortOptions.GainsHighToLow:
            return sorted.sort((a, b) => (b as ImpactProviderMapResults).gainsPercent - (a as ImpactProviderMapResults).gainsPercent);
          case ProvidersSortOptions.GainsLowToHigh:
            return sorted.sort((a, b) => (a as ImpactProviderMapResults).gainsPercent - (b as ImpactProviderMapResults).gainsPercent);
          case ProvidersSortOptions.ImpactsHighToLow:
            return sorted.sort((a, b) => (b as ImpactProviderMapResults).impactsPercent - (a as ImpactProviderMapResults).impactsPercent);
          case ProvidersSortOptions.ImpactsLowToHigh:
            return sorted.sort((a, b) => (a as ImpactProviderMapResults).impactsPercent - (b as ImpactProviderMapResults).impactsPercent);
          default:
            return sorted;
        }
      }
    );

  const contextValue: NetworkCoverageMapDataContextProps = {
    countyBorderData,
    currentAdequacySummarySpecialtyGroup,
    currentMapBoundary,
    dataBoundingBox,
    dataLoaded,
    dataRefreshCounter,
    filterImpactProvidersByNetworkAndActionType,
    getRegionDetails,
    impactProviders,
    isProviderPopupOpen,
    loadingMembers,
    loadingProviders,
    mapStyle,
    memberData,
    networkProviders,
    passPercentage,
    providerAccordionSelectedTab,
    providerFetchMessage,
    providerSearchText,
    resetMapData,
    selectedProvider,
    selectedProviderFilterOptions,
    selectedProviderSortOption,
    selectedProviderSourceOfSelection,
    setCurrentAdequacySummarySpecialtyGroup,
    setCurrentMapBoundary,
    setDataRefreshCounter,
    setImpactProviders,
    setIsProviderPopupOpen,
    setLoadingProviders,
    setMapStyle,
    setNetworkProviders,
    setPassPercentage,
    setProviderAccordionSelectedTab,
    setProviderFetchMessage,
    setProviderSearchText,
    setSelectedProvider,
    setSelectedProviderFilterOptions,
    setSelectedProviderSortOption,
    setSelectedProviderSourceOfSelection,
    setShowMembersWithAccess,
    setShowMembersWithoutAccess,
    setVisibleImpactProviders,
    setVisibleNetworkProviders,
    showMembersWithAccess,
    showMembersWithoutAccess,
    sortProviders,
    visibleImpactProviders,
    visibleNetworkProviders,
  };

  return (
    <NetworkCoverageMapDataContext.Provider value={contextValue}>
      {props.children}
    </NetworkCoverageMapDataContext.Provider>
  )
}

export { NetworkCoverageMapDataContext, NetworkCoverageMapDataContextProvider, NetworkCoverageMapDataContextProps };